import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Bell, Plus, Trash2, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

export default function MyAlertsPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Magic-link request state
  const [email, setEmail] = useState('');
  const [linkStatus, setLinkStatus] = useState(null); // null | 'success' | 'error'
  const [linkMsg, setLinkMsg] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  // Alerts list state
  const [alerts, setAlerts] = useState([]);
  const [alertsEmail, setAlertsEmail] = useState('');
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState('');

  // Add-alert form state
  const [newProc, setNewProc] = useState('');
  const [newState, setNewState] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [addStatus, setAddStatus] = useState(null);
  const [addMsg, setAddMsg] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    if (token) {
      loadAlerts();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAlerts = async () => {
    setAlertsLoading(true);
    setAlertsError('');
    try {
      const res = await axios.get(`${API}/alerts/my-alerts`, { params: { token } });
      setAlerts(res.data.alerts || []);
      setAlertsEmail(res.data.email || '');
    } catch (e) {
      setAlertsError(e.response?.data?.detail || 'Invalid or expired link. Request a new one below.');
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleRequestLink = async (e) => {
    e.preventDefault();
    setLinkLoading(true);
    setLinkStatus(null);
    try {
      await axios.post(`${API}/alerts/request-link`, { email });
      setLinkStatus('success');
      setLinkMsg('Check your email for a verification link.');
    } catch (e) {
      setLinkStatus('error');
      setLinkMsg(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleRemoveAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`, { params: { token } });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) {
      alert('Failed to remove alert. Please try again.');
    }
  };

  const handleAddAlert = async (e) => {
    e.preventDefault();
    if (!newProc.trim()) return;
    setAddLoading(true);
    setAddStatus(null);
    try {
      const res = await axios.get(`${API}/alerts/my-alerts`, { params: { token } });
      const currentEmail = res.data.email;
      await axios.post(`${API}/alerts/subscribe`, {
        email: currentEmail,
        procedure_keyword: newProc.trim(),
        state: newState || null,
        max_price_threshold: newPrice ? parseFloat(newPrice) : null,
      });
      setAddStatus('success');
      setAddMsg('Alert added!');
      setNewProc('');
      setNewState('');
      setNewPrice('');
      await loadAlerts();
    } catch (e) {
      setAddStatus('error');
      setAddMsg(e.response?.data?.detail || 'Failed to add alert.');
    } finally {
      setAddLoading(false);
    }
  };

  const hasToken = !!token;
  const tokenInvalid = hasToken && !alertsLoading && alertsError;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EEE9' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#0D3D4A' }} className="py-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Bell size={28} className="text-white/80" />
            <h1
              className="text-3xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              My Price Alerts
            </h1>
          </div>
          <p className="text-white/70 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Get notified when procedure prices drop to your threshold
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">

        {/* Token present + loading */}
        {hasToken && alertsLoading && (
          <div className="flex items-center justify-center gap-2 py-12 text-stone-500">
            <Loader2 size={20} className="animate-spin" />
            <span style={{ fontFamily: "'DM Sans', sans-serif" }}>Loading your alerts…</span>
          </div>
        )}

        {/* Token invalid → fall through to request form */}
        {tokenInvalid && (
          <div className="bg-amber-50 border border-amber-200 rounded p-4 text-amber-800 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            <AlertCircle size={16} className="inline mr-2" />
            {alertsError} Request a new magic link below.
          </div>
        )}

        {/* Show alert list when token is valid and loaded */}
        {hasToken && !alertsLoading && !alertsError && (
          <>
            {/* Active alerts */}
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
                <h2
                  className="text-lg font-semibold text-stone-800"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Active Alerts
                </h2>
                {alertsEmail && (
                  <span className="text-xs text-stone-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {alertsEmail}
                  </span>
                )}
              </div>

              {alerts.length === 0 ? (
                <div className="px-6 py-10 text-center text-stone-400 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  No active alerts. Add one below.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    <thead>
                      <tr className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wide">
                        <th className="px-6 py-3 text-left">Procedure</th>
                        <th className="px-4 py-3 text-left">Location</th>
                        <th className="px-4 py-3 text-left">Max Price</th>
                        <th className="px-4 py-3 text-left">Created</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {alerts.map(alert => (
                        <tr key={alert.id} className="hover:bg-stone-50 transition-colors">
                          <td className="px-6 py-4 text-stone-800 font-medium">
                            {alert.procedure_keyword || alert.cpt_code || '—'}
                          </td>
                          <td className="px-4 py-4 text-stone-600">{alert.state || 'Any'}</td>
                          <td className="px-4 py-4 text-stone-600">
                            {alert.max_price_threshold ? `$${alert.max_price_threshold.toLocaleString()}` : 'Any'}
                          </td>
                          <td className="px-4 py-4 text-stone-400 text-xs">
                            {alert.created_at ? new Date(alert.created_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <button
                              onClick={() => handleRemoveAlert(alert.id)}
                              className="text-red-400 hover:text-red-600 transition-colors p-1 rounded"
                              title="Remove alert"
                            >
                              <Trash2 size={15} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Add new alert */}
            <div className="bg-white border border-stone-200 rounded-lg shadow-sm p-6">
              <h2
                className="text-lg font-semibold text-stone-800 mb-4"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Add a New Alert
              </h2>
              <form onSubmit={handleAddAlert} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    Procedure name *
                  </label>
                  <input
                    type="text"
                    value={newProc}
                    onChange={e => setNewProc(e.target.value)}
                    placeholder="e.g. MRI knee, colonoscopy, cataract surgery"
                    required
                    className="w-full border border-stone-200 px-3 py-2 text-sm rounded focus:outline-none focus:ring-2 focus:ring-[#0D3D4A]/20 focus:border-[#0D3D4A]"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      State (optional)
                    </label>
                    <select
                      value={newState}
                      onChange={e => setNewState(e.target.value)}
                      className="w-full border border-stone-200 px-3 py-2 text-sm rounded focus:outline-none focus:ring-2 focus:ring-[#0D3D4A]/20 focus:border-[#0D3D4A]"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      <option value="">Any state</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      Max price threshold (optional)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        value={newPrice}
                        onChange={e => setNewPrice(e.target.value)}
                        placeholder="e.g. 500"
                        className="w-full border border-stone-200 pl-7 pr-3 py-2 text-sm rounded focus:outline-none focus:ring-2 focus:ring-[#0D3D4A]/20 focus:border-[#0D3D4A]"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      />
                    </div>
                  </div>
                </div>

                {addStatus && (
                  <div
                    className={`flex items-center gap-2 text-sm p-3 rounded ${
                      addStatus === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {addStatus === 'success' ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
                    {addMsg}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={addLoading || !newProc.trim()}
                  className="flex items-center gap-2 bg-[#0D3D4A] text-white px-5 py-2.5 text-sm font-medium rounded hover:bg-[#0E4557] transition-colors disabled:opacity-50"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {addLoading ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                  Add Alert
                </button>
              </form>
            </div>
          </>
        )}

        {/* Email request section — shown when no valid token */}
        {(!hasToken || tokenInvalid) && !alertsLoading && (
          <div className="bg-white border border-stone-200 rounded-lg shadow-sm p-8 max-w-md mx-auto text-center">
            <div className="w-12 h-12 rounded-full bg-[#0D3D4A]/10 flex items-center justify-center mx-auto mb-4">
              <Bell size={22} style={{ color: '#0D3D4A' }} />
            </div>
            <h2
              className="text-xl font-semibold text-stone-800 mb-2"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Access Your Alerts
            </h2>
            <p className="text-stone-500 text-sm mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Enter your email and we'll send you a secure link to view and manage your price alerts.
            </p>
            <form onSubmit={handleRequestLink} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full border border-stone-200 px-4 py-2.5 text-sm rounded focus:outline-none focus:ring-2 focus:ring-[#0D3D4A]/20 focus:border-[#0D3D4A]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              />
              {linkStatus && (
                <div
                  className={`flex items-center gap-2 text-sm p-3 rounded ${
                    linkStatus === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {linkStatus === 'success' ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
                  {linkMsg}
                </div>
              )}
              <button
                type="submit"
                disabled={linkLoading}
                className="w-full bg-[#0D3D4A] text-white px-5 py-2.5 text-sm font-medium rounded hover:bg-[#0E4557] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {linkLoading && <Loader2 size={15} className="animate-spin" />}
                Send Magic Link
              </button>
            </form>
            <p className="text-xs text-stone-400 mt-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Don't have alerts yet?{' '}
              <a href="/procedures" className="text-[#0D3D4A] hover:underline">
                Browse procedures
              </a>{' '}
              to set one up.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
